import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { UserContext } from "App";
import { GetNewsAPI } from "APIs/NewsAPIs";
import { DeleteNewsAPI } from "APIs/NewsAPIs";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";
import MDBadge from "components/MDBadge";
import MDInput from "components/MDInput";
import DataTable from "templates/Tables/DataTable";
import { popUp, deleteMsg } from "components/Custom/AreYouSurePopUp";
// Receipt page theme (shared)
import {
  SectionHeader,
  ranuButtonSx,
  ranuOutlinedButtonSx,
} from "layouts/receipt/components/SectionCard";

// Ranu gold, same focus colour the cards give to their inputs (the search box sits outside a card)
const FOCUS_GOLD = "#C89B3C";

const searchFocusSx = {
  "& .MuiOutlinedInput-root.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline": {
    borderColor: FOCUS_GOLD,
    borderWidth: "2px",
  },
  "& .MuiInputBase-root.Mui-focused": { boxShadow: "none", outline: "none" },
};

const viewButtonSx = ({ palette }) => ({
  color: palette.white.main,
  boxShadow: "none",
  transition: "background-color .3s ease, color .3s ease",
  "&:hover": {
    backgroundColor: palette.primary.main,
    color: palette.white.main,
    boxShadow: "none",
  },
  "&:focus:not(:hover)": { boxShadow: "none" },
});

// View, Edit and Delete all get the same fixed size
// (explicit width + height + border-box, so the outlined buttons' 1px border can't make them bigger)
const statusButtonSx = {
  // smaller on phones/tablets/laptops, full size only on wide screens (xxl >= 1400px)
  width: { xs: "4.75rem", xxl: "5.875rem" },
  minWidth: 0,
  height: { xs: "2.25rem", xxl: "2.5rem" },
  minHeight: { xs: "2.25rem", xxl: "2.5rem" },
  fontSize: { xs: "0.7rem", xxl: "0.75rem" },
  boxSizing: "border-box",
  padding: 0,
  lineHeight: 1,
};

// Responsive rules for the table (CSS only, no logic). Same as the Devotee table.
// - the template's table cells use `width: max-content` and big side padding, so on laptops,
//   tablets and phones the Action column was pushed outside the card. Here the cells may
//   shrink / wrap instead.
// - on phones every row becomes a card (label on the left, value on the right)
// - table header text uses the theme primary (maroon) instead of the template's blue
const tableSx = ({ palette }) => ({
  "& thead th > div": { color: palette.primary.main },

  // Laptops / small desktops (< 1400px): tighter cells so the Action column always fits
  "@media (max-width: 1399.98px)": {
    "& th, & td": { paddingLeft: "10px !important", paddingRight: "10px !important" },
    "& td > div": { width: "auto" }, // long titles wrap instead of stretching the table
    "& td:last-of-type > div": { maxWidth: "16rem" },
  },

  // Desktop / large tablet (>= 900px): View, Edit and Delete always stay in ONE row
  "@media (min-width: 900px)": {
    "& td:last-of-type > div > div": { flexWrap: "nowrap" },
  },

  // Tablet and below (< 992px)
  "@media (max-width: 991.98px)": {
    "& th, & td": { paddingLeft: "8px !important", paddingRight: "8px !important" },
    "& td > div": { width: "auto" }, // lets the action buttons wrap
  },

  // Phones and small tablets (< 900px): every row becomes a card
  "@media (max-width: 899.98px)": {
    "& table, & tbody, & tr, & td": {
      display: "block",
      width: "100%",
      boxSizing: "border-box",
    },
    "& thead": { display: "none" },

    "& tbody tr": {
      boxSizing: "border-box",
      width: "auto",
      margin: "12px",
      padding: "8px 4px",
      border: `1px solid ${palette.light.main}`,
      borderRadius: "12px",
      background: palette.white.main,
    },

    "& td": {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: "12px",
      textAlign: "right",
      borderBottom: "none",
      padding: "6px 12px !important",
    },
    "& td::before": {
      content: "attr(data-label)",
      textTransform: "uppercase",
      fontWeight: 700,
      fontSize: "0.7rem",
      color: palette.primary.main,
      textAlign: "left",
      flexShrink: 0,
    },
    "& td > div": { width: "auto", maxWidth: "65%", wordBreak: "break-word" },

    // Action cell: no label, the 3 buttons sit side by side and share the full width
    "& td:last-of-type::before": { display: "none" },
    "& td:last-of-type > div": { width: "100%", maxWidth: "100%" },
    "& td:last-of-type > div > div": { flexWrap: "nowrap", gap: "8px" },
    "& td:last-of-type .MuiButton-root": {
      flex: "1 1 0",
      width: "auto",
      minWidth: 0,
      fontSize: "0.7rem",
      whiteSpace: "nowrap",
    },
  },

  // Very small phones (<= 400px, e.g. 300-360px): tighter cards so nothing is cut off
  "@media (max-width: 400px)": {
    "& tbody tr": { margin: "8px 6px" },
    "& td": { padding: "6px 8px !important", gap: "8px" },
    "& td > div": { maxWidth: "60%" },
    "& td:last-of-type > div": { maxWidth: "100%" },
    // still ONE row of 3 buttons, just a little tighter so it fits 300px screens
    "& td:last-of-type > div > div": { gap: "6px" },
    "& td:last-of-type .MuiButton-root": { fontSize: "0.62rem", letterSpacing: 0 },
  },
});

const Index = () => {
  const userData = useContext(UserContext);
  const dispatch = useDispatch();
  const [news, setNews] = useState([]);
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [findText, setFindText] = useState("");
  const navigate = useNavigate();

  const getAllNewsFunc = async (access_token, dispatch) => {
    setIsLoading(true);
    const response = await GetNewsAPI(access_token, dispatch);
    if (response !== null) {
      // API can return the list directly or inside an object, always keep an array in state
      const list = Array.isArray(response)
        ? response
        : Array.isArray(response?.data?.list)
        ? response.data.list
        : Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.list)
        ? response.list
        : [];
      setNews(list);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (userData.access_token) {
      getAllNewsFunc(userData.access_token, dispatch);
    }
  }, [userData]);

  // Client-side search: matches the title or the status text (active / inactive)
  const filteredNews = useMemo(() => {
    const list = Array.isArray(news) ? news : [];
    const q = findText.trim().toLowerCase();
    if (!q) return list;
    return list.filter((item) => {
      const title = (item.news_title || "").toLowerCase();
      const status = item.status == 0 ? "inactive" : "active";
      return title.includes(q) || status.includes(q);
    });
  }, [news, findText]);

  useEffect(() => {
    setRows([
      ...filteredNews.map((item, key) => {
        return {
          sr: `${key + 1}`,
          title: item.news_title,

          status: (
            <MDBadge
              badgeContent={item.status == 0 ? "INACTIVE" : "ACTIVE"}
              size="sm"
              color={item.status == 0 ? "secondary" : "success"}
              variant="contained"
              container
            />
          ),
          action: (
            // flex + wrap: on small screens the buttons go to the next line instead of overflowing
            <MDBox display="flex" flexWrap="wrap" justifyContent="center" gap={1}>
              <MDButton
                color="secondary"
                sx={[viewButtonSx, statusButtonSx]}
                onClick={() => {
                  navigate(`view/${item.news_id}`);
                }}
              >
                view
              </MDButton>
              <MDButton
                variant="outlined"
                color="primary"
                sx={[ranuOutlinedButtonSx, statusButtonSx]}
                onClick={() => {
                  navigate(`edit/${item.news_id}`);
                }}
              >
                Edit
              </MDButton>
              <MDButton
                color="error"
                sx={statusButtonSx}
                onClick={async (e) => {
                  await popUp(handleDelete, item.news_id, deleteMsg("news"));
                }}
              >
                Delete
              </MDButton>
            </MDBox>
          ),
        };
      }),
    ]);
    setColumns([
      { Header: "sr no", accessor: "sr", align: "center", width: "10%" },
      { Header: "title", accessor: "title" },
      { Header: "action", accessor: "action", align: "center" },
    ]);
  }, [filteredNews]);

  const handleDelete = async (id) => {
    const response = await DeleteNewsAPI(
      {
        news_id: id,
      },
      userData.access_token,
      dispatch
    );
    if (response !== null) {
      getAllNewsFunc(userData.access_token, dispatch);
    }
  };

  return (
    <MDBox px={{ xs: 1, md: 3 }} pt={{ xs: 0, md: 1 }} pb={3}>
      {/* Add button sits outside the table, top right (next to the page badge) */}
      <SectionHeader
        icon="newspaper"
        label="NEWS"
        endAction={
          <MDButton
            color="primary"
            sx={ranuButtonSx}
            onClick={() => {
              navigate("/news/add");
            }}
          >
            <MDTypography fontSize={15} fontWeight="bold" color="inherit" noWrap>
              Add News
            </MDTypography>
          </MDButton>
        }
      />

      {/* The table has its own padding, so cancel the card padding around it */}
      <MDBox mx={{ xs: -2, md: -3 }} mb={{ xs: -2, md: -3 }} sx={tableSx}>
        <DataTable
          isLoading={isLoading}
          table={{
            columns: columns,
            rows: rows,
          }}
          isSorted={false}
          showTotalEntries={true}
          noEndBorder
          FindText={
            <MDInput
              placeholder="Search..."
              value={findText}
              size="small"
              fullWidth
              sx={searchFocusSx}
              onChange={(e) => setFindText(e.target.value)}
            />
          }
          entriesPerPage={{ defaultValue: 10, entries: [10, 25, 50] }}
          showPagination
        />
      </MDBox>
    </MDBox>
  );
};

export default Index;
